<template>
  <div>
    <AffiliateHeaderContentSkeleton v-if="isLoading" />

    <template v-else-if="!isLoading && affiliate">
      <div class="-mx-16 mb-16 border-y border-line bg-gray-200 py-8">
        <div class="flex items-center justify-between px-16">
          <div class="flex items-center gap-4">
            <div
              class="flex h-40 w-40 items-center justify-center rounded-full border border-disabled"
            >
              <JIcon name="gift" size="20" class="text-gray-500" />
            </div>

            <div class="flex flex-col gap-4">
              <span class="text-metadata-limited font-medium">
                {{ $t('affiliateWallet') }}
              </span>
              <span
                v-if="affiliate.status !== 'Pending'"
                class="text-subject font-bold text-primary"
              >
                {{
                  formatCurrency(
                    affiliate.currentBalance,
                    currencyStore.currency.symbol
                  )
                }}
              </span>
              <span v-else class="text-subject font-bold text-warning">
                {{ $t('affiliate.messages.pending') }}
              </span>
            </div>
          </div>

          <AffiliateTransactionsToggler v-if="affiliate.status !== 'Pending'">
            <JButton
              size="sm"
              color="secondary"
              variant="tonal"
              class="h-max"
              @click="affiliateStore.toggleAffiliateTransactionsModal"
            >
              <span class="font-medium">
                {{ $t('transactions') }}
              </span>
              <JIcon name="arrow-right-outline" size="16" />
            </JButton>
          </AffiliateTransactionsToggler>
        </div>
      </div>

      <div
        v-if="affiliate.status !== 'Pending'"
        class="flex items-center justify-between md:mb-16"
      >
        <span class="text-gray-500">
          {{ $t('yourAffiliateCode') }}
        </span>

        <JButton
          v-if="affiliate.status === 'Active'"
          color="dark"
          size="sm"
          class="h-max bg-black text-white"
          @click="copyAffiliateCode(affiliate.affiliateCode)"
        >
          <JIcon name="copy" size="16" />
          <span class="font-medium">
            {{ affiliate.affiliateCode ?? '---' }}
          </span>
        </JButton>

        <JButton
          v-if="affiliate.status === 'Inactive'"
          color="secondary"
          variant="tonal"
          size="sm"
          disabled
        >
          <span class="font-medium">
            {{ $t('affiliate.deactivated') }}
          </span>
        </JButton>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { JButton, JIcon } from '@journey/ui-kit'
import { useClipboard } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import type { Props } from './AffiliateHeaderContent.types'
import AffiliateHeaderContentSkeleton from './AffiliateHeaderContentSkeleton/AffiliateHeaderContentSkeleton.vue'
import useAffiliateStore from '@/modules/Affiliate/store'
import useCurrencyStore from '@/modules/Core/store/currency'
import useToastStore from '@/modules/Core/store/toast'
import AffiliateTransactionsToggler from '@/modules/Affiliate/components/AffiliateTransactionsToggler/AffiliateTransactionsToggler.vue'
import { useCurrency } from '@/modules/Core/composables'

defineProps<Props>()

const affiliateStore = useAffiliateStore()

const currencyStore = useCurrencyStore()

const { copy } = useClipboard()
const toastStore = useToastStore()
const { t } = useI18n()

async function copyAffiliateCode(affiliateCode: string) {
  await copy(affiliateCode)

  toastStore.addToast({
    color: 'success',
    title: t('copyTextMessage'),
  })
}

const { formatCurrency } = useCurrency()
</script>
